// GST setup checks for admin (gst-parked task 030, owner decisions D4/D5).
//
// India GST regime only — callers pass `isIndia` from useTaxRegime(), and a VAT
// business never gets a warning. The API makes the same checks
// (TenantsService.assertGstState) and the same "registered" answer
// (common/utils/gst-registration.ts); the POS till has its own copy in
// apps/pos/src/lib/gst-setup.ts.

/** GSTIN state codes (first two digits) → state / UT name (GST Network list). */
const GST_STATE_CODES: Record<string, string> = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
};
/** Old codes that now belong to another state (old Andhra Pradesh, old Daman & Diu). */
const CODE_ALIASES: Record<string, string> = { '28': '37', '25': '26' };

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z]/g, '');

/**
 * The message to show when a GSTIN's state code is not the chosen State, or
 * null when they agree (or either is blank / unknown).
 */
export function gstinStateMismatch(gstin: string, state: string): string | null {
  const g = gstin.trim().toUpperCase();
  if (!/^[0-9]{2}/.test(g) || !state.trim()) return null;
  const code = CODE_ALIASES[g.slice(0, 2)] ?? g.slice(0, 2);
  const expected = GST_STATE_CODES[code];
  if (!expected || norm(expected) === norm(state)) return null;
  return `Your GSTIN starts with ${g.slice(0, 2)}, which is ${expected}. Your State is ${state}. Change the GSTIN or the State so they match.`;
}

/** "GST registered": the owner's choice, else registered iff a GSTIN is set. */
export function deriveGstRegistered(
  setting: boolean | null | undefined,
  gstin: string | null | undefined,
): boolean {
  if (setting === true || setting === false) return setting;
  return !!gstin?.trim();
}

export interface GstSetupInput {
  isIndia: boolean;
  /** The owner's stored choice; null / undefined = derived from the GSTIN. */
  setting: boolean | null | undefined;
  gstin: string | null | undefined;
  state: string | null | undefined;
  locations: ReadonlyArray<{ name: string; is_active: boolean; state?: string | null }>;
}

export type GstSetupWarning =
  /** No GSTIN and no choice made: Orio360 treats the business as not registered. */
  | { kind: 'UNREGISTERED_NO_GSTIN' }
  /** Registered, but something the tax type needs is missing. */
  | { kind: 'MISSING'; gstin: boolean; state: boolean; locations: string[] };

export function gstSetupWarning(input: GstSetupInput): GstSetupWarning | null {
  if (!input.isIndia) return null;
  const registered = deriveGstRegistered(input.setting, input.gstin);
  if (!registered) {
    // An owner who said "not registered" has nothing to fix.
    return input.setting === false ? null : { kind: 'UNREGISTERED_NO_GSTIN' };
  }
  const gstin = !input.gstin?.trim();
  const state = !input.state?.trim();
  const locations = input.locations
    .filter((l) => l.is_active && !l.state?.trim())
    .map((l) => l.name);
  return gstin || state || locations.length > 0
    ? { kind: 'MISSING', gstin, state, locations }
    : null;
}

/** Title and short sentences for a warning (age-10 English). */
export function gstSetupText(w: GstSetupWarning): { title: string; lines: string[] } {
  if (w.kind === 'UNREGISTERED_NO_GSTIN') {
    return {
      title: 'Not GST registered',
      lines: [
        'You have not added a GSTIN, so Orio360 treats your business as not GST registered.',
        'Are you registered? Add your GSTIN and your State.',
      ],
    };
  }
  const lines: string[] = [];
  if (w.gstin) lines.push('Add your GSTIN. It goes on every bill.');
  if (w.state) lines.push('Pick the State your business is in.');
  if (w.locations.length > 0)
    lines.push(`Pick a State for these stores: ${w.locations.join(', ')}.`);
  lines.push('Until then, Orio360 bills each sale as same-state, with CGST and SGST.');
  return { title: 'Set your GSTIN / state', lines };
}
